import { Currency } from "_shared/money/currencies/currency";
import {
  HumanReadableProposalStatus,
  ProposalEvent,
  ProposalStatus,
} from "work/proposals/__constants/proposal-lifecycle";
import { WorkContext } from "work/work-agreements/_api/queries/_fields/work-context";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";
import WorkRecordCriterion, {
  WorkRecordCriterionKind,
  type WorkRecordCriterionValue,
  type WorkRecordCriterionYesNo,
} from "work/work-records/work-record-criterion";
import { WorkTeamRole } from "work/work-records/work-team-role";

/** A criterion set in a URL, with its value. */
export type ActiveWorkRecordCriterion = Readonly<{
  criterion: WorkRecordCriterion;
  value: WorkRecordCriterionValue;
}>;

const text = (property: string, label: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Text, label });
const id = (property: string, label: string, parameter?: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Id, label, parameter });
const person = (property: string, label: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Person, label });
const firm = (property: string, label: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Firm, label });
const yesNo = (property: string, label: string, both: WorkRecordCriterionYesNo) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.YesNo, label, yesNo: both });
const instant = (property: string, label: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Instant, label });
const amount = (property: string, label: string) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Amount, label });
const choice = (property: string, label: string, choices: Readonly<Record<string, string>>) =>
  new WorkRecordCriterion({ property, kind: WorkRecordCriterionKind.Choice, label, choices });

/** Each value of a string enum, called by its own name. */
function namedAsTheyAre(values: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.values(values).map((value) => [value, value]));
}

/** Each value of a string enum, called by its words: `SubmittedByCreator` is "Submitted by creator". */
function namedByTheirWords(values: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(
    Object.values(values).map((value) => {
      const words = value.replaceAll(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
      return [value, words.charAt(0).toUpperCase() + words.slice(1)];
    }),
  );
}

const PROPOSAL_STATUSES: Record<string, string> = Object.fromEntries(
  Object.values(ProposalStatus).map((status) => [
    status,
    HumanReadableProposalStatus[status as keyof typeof HumanReadableProposalStatus] ?? status,
  ]),
);

/**
 * The criteria every work record shares: `Work.WorkRecords.WorkCriteria`, less what the server sets.
 */
const SHARED_CRITERIA: readonly WorkRecordCriterion[] = [
  text("query", "Search"),
  id("RFPId", "Request", "rfpId"),
  text("name", "Name"),
  text("description", "Description"),
  person("clientUserId", "Client team leader"),
  firm("clientEntityId", "Client firm"),
  person("clientTeamMemberUserId", "Client team member"),
  firm("clientTeamMemberEntityId", "Client team member's firm"),
  instant("minStart", "Starts on or after"),
  instant("maxStart", "Starts on or before"),
  yesNo("hasEnd", "End date", { yes: "Has an end date", no: "No end date" }),
  instant("minEnd", "Ends on or after"),
  instant("maxEnd", "Ends on or before"),
  instant("inProgressOn", "In progress on"),
  yesNo("vendorTeamRestricted", "Vendor team restricted", {
    yes: "Vendor team restricted",
    no: "Vendor team not restricted",
  }),
  // Not the vendor team's leader, whatever the name says: the API matches an additional vendor.
  person("vendorUserId", "Additional vendor"),
  firm("vendorEntityId", "Vendor firm"),
  firm("vendorTeamMemberEntityId", "Vendor team member's firm"),
  id("vendorTeamMarketplaceId", "Marketplace team"),
  person("additionalVendorUserId", "Additional vendor"),
  firm("additionalVendorEntityId", "Additional vendor's firm"),
  person("teamMemberUserId", "On a team"),
  choice("teamSide", "Team", { [WorkTeamSide.Client]: "Client team", [WorkTeamSide.Vendor]: "Vendor team" }),
  choice("teamRole", "Seat", { [WorkTeamRole.Leader]: "Leader", [WorkTeamRole.Member]: "Member" }),
  firm("counterpartyEntityId", "Other party"),
  id("feeScheduleCategoryId", "Fee category"),
  text("feeScheduleCategoryName", "Fee category"),
  text("feeScheduleCategoryDescription", "Fee category description"),
  yesNo("feeIsFixed", "Fixed fee", { yes: "Fixed fee", no: "Not a fixed fee" }),
  yesNo("feeIsHourly", "Hourly rate", { yes: "Hourly rate", no: "Not an hourly rate" }),
  amount("minFeeRateAmount", "Rate at least"),
  amount("maxFeeRateAmount", "Rate at most"),
  choice("feeRateCurrency", "Currency", namedAsTheyAre(Currency)),
  person("feeAssignedMemberUserId", "Fee assigned to"),
  id("teamTemplateId", "Team template"),
  text("teamTemplateName", "Team template"),
  id("feeScheduleTemplateId", "Fee schedule template"),
  text("feeScheduleTemplateName", "Fee schedule template"),
  amount("minDiscount", "Discount at least"),
  amount("maxDiscount", "Discount at most"),
  person("userId", "On either team"),
];

/** What only a proposal list reads: `Work.Proposals.ProposalFilterParameters`, less what the page decides. */
const PROPOSAL_CRITERIA: readonly WorkRecordCriterion[] = [
  choice("status", "Status", PROPOSAL_STATUSES),
  yesNo("isActive", "Active", { yes: "Active", no: "Not active" }),
  yesNo("hasStart", "Start date", { yes: "Has a start date", no: "No start date" }),
  person("vendorTeamMemberId", "Vendor team member"),
  instant("minCreated", "Created on or after"),
  instant("maxCreated", "Created on or before"),
  yesNo("isNegotiable", "Negotiable", { yes: "Negotiable", no: "Not negotiable" }),
  yesNo("hasResponseDueBy", "Response due date", { yes: "Has a response due date", no: "No response due date" }),
  instant("minResponseDueBy", "Response due on or after"),
  instant("maxResponseDueBy", "Response due on or before"),
  instant("minUpdated", "Updated on or after"),
  instant("maxUpdated", "Updated on or before"),
  person("clientReviewerId", "Client reviewer"),
  person("vendorReviewerId", "Vendor reviewer"),
  choice("event", "Event", namedByTheirWords(ProposalEvent)),
  person("eventActorUserId", "Event by"),
  instant("minEventDate", "Event on or after"),
  instant("maxEventDate", "Event on or before"),
];

/** What only a work agreement list reads: `Work.WorkAgreements.WorkAgreementFilterParameters`, less the tab. */
const WORK_AGREEMENT_CRITERIA: readonly WorkRecordCriterion[] = [
  yesNo("isActive", "Active", { yes: "Active work", no: "Not active work" }),
  instant("minHireDate", "Hired on or after"),
  instant("maxHireDate", "Hired on or before"),
  person("vendorTeamMemberUserId", "Vendor team member"),
  choice("context", "Your side", {
    [WorkContext.Client]: "Client",
    [WorkContext.Vendor]: "Vendor",
    [WorkContext.None]: "Neither",
  }),
  yesNo("currentVersionOnly", "Versions", { yes: "Latest version only", no: "All versions" }),
  id("versionsOf", "Versions of one work"),
  instant("minArchived", "Archived on or after"),
  instant("maxArchived", "Archived on or before"),
];

/**
 * The criteria a list page reads from its URL, and the chips it shows for them.
 *
 * The other half of `Work.WorkRecords.WorkRecordListLink`, which writes the assistant's `listUrl`
 * from the same names. `work-record-list-links.json` is the fixture the two are held to.
 *
 * The page's tab, its proposal box, its sort and its paging are not criteria here: the page reads
 * them itself.
 */
export default class WorkRecordCriteria {
  public static readonly PROPOSALS = new WorkRecordCriteria([...SHARED_CRITERIA, ...PROPOSAL_CRITERIA]);
  public static readonly WORK_AGREEMENTS = new WorkRecordCriteria([...SHARED_CRITERIA, ...WORK_AGREEMENT_CRITERIA]);

  public readonly criteria: readonly WorkRecordCriterion[];

  public constructor(criteria: readonly WorkRecordCriterion[]) {
    this.criteria = criteria;
  }

  /** The criterion a URL carries under `parameter`, if this list reads one. */
  public byParameter(parameter: string): WorkRecordCriterion | undefined {
    return this.criteria.find((criterion) => criterion.parameter === parameter);
  }

  /** Every criterion `searchParams` sets, in the order the chips show them. */
  public active(searchParams: URLSearchParams): ActiveWorkRecordCriterion[] {
    const active: ActiveWorkRecordCriterion[] = [];
    for (const criterion of this.criteria) {
      const value = criterion.read(searchParams);
      if (value !== undefined) active.push({ criterion, value });
    }
    return active;
  }

  /** Every criterion `searchParams` sets, keyed by the property the query class holds it in. */
  public read(searchParams: URLSearchParams): Record<string, WorkRecordCriterionValue> {
    return Object.fromEntries(this.active(searchParams).map(({ criterion, value }) => [criterion.property, value]));
  }

  /**
   * `searchParams` without `criterion`, back on the first page.
   *
   * The first page because the list it was on is not the list it is now: page four of the old
   * list can be past the end of the new one.
   */
  public without(searchParams: URLSearchParams, criterion: WorkRecordCriterion): URLSearchParams {
    const next = new URLSearchParams(searchParams);
    next.delete(criterion.parameter);
    if (next.has("pageIndex")) next.set("pageIndex", "0");
    return next;
  }

  /**
   * `into`, with every criterion `from` sets that the page's own controls do not own.
   *
   * The table rebuilds its URL from its own state — the search box, the column filters, the sort
   * and the page — and the criteria a link brought have no control there. Without this, sorting a
   * list a link opened would drop the link's criteria and show a different list.
   *
   * @param owned The parameters the table writes itself. A criterion among them is the table's to
   *   keep or clear, so it is not carried over: clearing a column filter has to clear it.
   */
  public carryOver(from: URLSearchParams, into: URLSearchParams, owned: readonly string[]): URLSearchParams {
    const next = new URLSearchParams(into);
    for (const criterion of this.criteria) {
      if (owned.includes(criterion.parameter) || next.has(criterion.parameter)) continue;
      const value = from.get(criterion.parameter);
      if (value !== null) next.set(criterion.parameter, value);
    }
    return next;
  }
}
